import { useCallback, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import { BuildEnv, Logger } from '@shared/utils'

const log = Logger('AppVersionWatcher')

const VERSION_URL = `${import.meta.env.BASE_URL}version.json`
// Deployed version we already reloaded for, and the flag that carries the toast across that reload.
const RELOAD_TARGET_KEY = 'app-version-reload-target'
const RELOAD_NOTICE_KEY = 'app-version-reload-notice'
const STALE_TOAST_ID = 'app-version-stale'

const currentVersion = import.meta.env.VITE_GIT_HASH
// The dev server emits no manifest, and locally the hash changes with every commit.
const enabled = import.meta.env.VITE_BUILD_ENV !== BuildEnv.Local && Boolean(currentVersion)

// Releases go out nightly, so asking more than once every few minutes cannot learn anything new.
// Without this, navigating around the admin app fires a request per route change, and the manifest
// is served `no-store` so every one of them is a full origin round trip.
const CHECK_THROTTLE_MS = 5 * 60 * 1000

// When the last check started, per document. A page load resets it to 0, so the boot check — the
// one that matters after a nightly release — always runs.
let lastCheckAt = 0

/**
 * The loop guard lives in sessionStorage so it survives the reload but not the tab. If it cannot
 * be persisted we must not reload at all — without it, a host that keeps serving the previous
 * index.html would reload the tab forever.
 *
 * @returns {boolean} whether the reload is safe to perform
 */
function rememberReload(version) {
  try {
    sessionStorage.setItem(RELOAD_TARGET_KEY, version)
    sessionStorage.setItem(RELOAD_NOTICE_KEY, '1')
    return true
  } catch {
    // Storage blocked (private mode, locked-down webview).
    return false
  }
}

function alreadyReloadedFor(version) {
  try {
    return sessionStorage.getItem(RELOAD_TARGET_KEY) === version
  } catch {
    return false
  }
}

function consumeReloadNotice() {
  try {
    if (!sessionStorage.getItem(RELOAD_NOTICE_KEY)) return false
    sessionStorage.removeItem(RELOAD_NOTICE_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * Reloads the app when a newer build has been deployed.
 *
 * After a deploy, a browser holding a cached index.html keeps running the previous bundle, which
 * then calls endpoints the backend has already moved or removed (QCP-7548). Nothing 404s on the
 * asset side in that case, so `staleChunkReload` never fires — the only way to see the mismatch
 * is to compare the hash this bundle was built with against the one currently deployed.
 *
 * Checks run at boot, on tab refocus and on navigation: the three moments a user can enter or
 * continue into a stale bundle. Releases go out at night when no client is connected, so an
 * interval would catch nothing these do not.
 *
 * Those triggers fire far more often than releases happen, so checks are throttled to one per
 * CHECK_THROTTLE_MS. The boot check is never throttled. An idle tab makes no requests at all.
 */
export default function AppVersionWatcher({
  updatedMessage = 'Updated to the latest version',
  staleMessage = 'A new version is available.',
  reloadLabel = 'Reload',
}) {
  const { pathname } = useLocation()
  const checking = useRef(false)
  const stopped = useRef(false)

  const check = useCallback(async () => {
    if (!enabled || stopped.current || checking.current) return
    if (document.visibilityState !== 'visible') return
    if (Date.now() - lastCheckAt < CHECK_THROTTLE_MS) return

    checking.current = true
    lastCheckAt = Date.now()

    try {
      // `no-store` covers browser and proxy caches. The `?t=` param is deliberate belt and braces —
      // it does nothing against Amplify's CloudFront, which ignores query strings, but costs nothing
      // and helps anywhere else. Edge freshness comes from the `Cache-Control: no-store` header
      // configured for version.json in the Amplify console.
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' })
      if (!response.ok) return

      const { version } = await response.json()
      if (!version || version === currentVersion) return

      // Either we already reloaded for this version and it did not take effect (the host is still
      // serving the old index.html), or we cannot persist the guard. Both cases would loop, so
      // hand it to the user instead — by the time they click, the deploy has likely propagated.
      if (alreadyReloadedFor(version) || !rememberReload(version)) {
        stopped.current = true
        toast(
          (t) => (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <span>{staleMessage}</span>
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                onClick={() => {
                  toast.dismiss(t.id)
                  window.location.reload()
                }}
              >
                {reloadLabel}
              </Button>
            </Stack>
          ),
          { id: STALE_TOAST_ID, duration: Infinity }
        )
        return
      }

      log.info('[new version]', currentVersion, '->', version)
      window.location.reload()
    } catch (e) {
      // Offline, request blocked, or this build has no manifest — nothing to act on.
      log.debug('[check failed]', e)
    } finally {
      checking.current = false
    }
  }, [staleMessage, reloadLabel])

  // A toast fired right before location.reload() would be wiped in the same frame, so the notice
  // is left behind and picked up here once the new bundle is running.
  useEffect(() => {
    if (!enabled) return
    if (consumeReloadNotice()) toast.success(updatedMessage)
  }, [updatedMessage])

  // Boot, and every navigation after it.
  useEffect(() => {
    check()
  }, [check, pathname])

  // Tab brought back to the foreground — covers a tab left open across a release.
  useEffect(() => {
    if (!enabled) return

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [check])

  return null
}
